import { useState, useEffect, useCallback } from "react";
import {
  MessageSquare, Loader2, RefreshCw, CheckCircle2, XCircle, AlertTriangle,
  Send, Phone, BarChart3, ArrowUpRight, ArrowDownRight, Wifi, WifiOff, Clock
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { format, startOfDay, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";

interface Props {
  restaurants: any[];
}

interface DailyStats {
  total: number;
  sent: number;
  failed: number;
  received: number;
}

interface RestaurantStats {
  restaurant_id: string;
  restaurant_name: string;
  total_sent: number;
  total_failed: number;
  connected: boolean;
}

const AdminWhatsAppTab = ({ restaurants }: Props) => {
  const [loading, setLoading] = useState(true);
  const [todayStats, setTodayStats] = useState<DailyStats>({ total: 0, sent: 0, failed: 0, received: 0 });
  const [weekStats, setWeekStats] = useState<DailyStats>({ total: 0, sent: 0, failed: 0, received: 0 });
  const [restaurantStats, setRestaurantStats] = useState<RestaurantStats[]>([]);
  const [recentMessages, setRecentMessages] = useState<any[]>([]);
  const [instances, setInstances] = useState<any[]>([]);
  const [evolutionStatus, setEvolutionStatus] = useState<'online' | 'offline' | 'checking'>('checking');
  const [lastCheck, setLastCheck] = useState<Date | null>(null);

  const checkEvolutionHealth = useCallback(async () => {
    setEvolutionStatus('checking');
    try {
      const res = await supabase.functions.invoke('test-evolution-connection', {
        body: {
          base_url: 'http://187.77.47.147:8080',
          api_key: 'viamenu25807913',
        },
      });
      setEvolutionStatus(res.data?.ok ? 'online' : 'offline');
    } catch {
      setEvolutionStatus('offline');
    }
    setLastCheck(new Date());
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    const today = startOfDay(new Date()).toISOString();
    const weekAgo = subDays(new Date(), 7).toISOString();

    // Load today's messages
    const { data: todayMsgs } = await supabase
      .from('whatsapp_messages')
      .select('id, status, direction')
      .gte('created_at', today);

    if (todayMsgs) {
      setTodayStats({
        total: todayMsgs.length,
        sent: todayMsgs.filter(m => m.status === 'sent' && m.direction === 'outbound').length,
        failed: todayMsgs.filter(m => m.status === 'failed').length,
        received: todayMsgs.filter(m => m.direction === 'inbound').length,
      });
    }

    // Load week messages
    const { data: weekMsgs } = await supabase
      .from('whatsapp_messages')
      .select('id, status, direction')
      .gte('created_at', weekAgo);

    if (weekMsgs) {
      setWeekStats({
        total: weekMsgs.length,
        sent: weekMsgs.filter(m => m.status === 'sent' && m.direction === 'outbound').length,
        failed: weekMsgs.filter(m => m.status === 'failed').length,
        received: weekMsgs.filter(m => m.direction === 'inbound').length,
      });
    }

    // Load instances
    const { data: inst } = await supabase
      .from('whatsapp_instances')
      .select('*');
    setInstances(inst || []);

    // Load per-restaurant stats (last 7 days)
    const { data: allMsgs } = await supabase
      .from('whatsapp_messages')
      .select('restaurant_id, status, direction')
      .gte('created_at', weekAgo)
      .eq('direction', 'outbound');

    if (allMsgs) {
      const map = new Map<string, { sent: number; failed: number }>();
      allMsgs.forEach(m => {
        const cur = map.get(m.restaurant_id) || { sent: 0, failed: 0 };
        if (m.status === 'sent') cur.sent++;
        if (m.status === 'failed') cur.failed++;
        map.set(m.restaurant_id, cur);
      });

      const stats: RestaurantStats[] = Array.from(map.entries())
        .map(([rid, s]) => ({
          restaurant_id: rid,
          restaurant_name: restaurants.find(r => r.id === rid)?.name || rid.slice(0, 8),
          total_sent: s.sent,
          total_failed: s.failed,
          connected: (inst || []).some((i: any) => i.restaurant_id === rid && i.status === 'connected'),
        }))
        .sort((a, b) => (b.total_sent + b.total_failed) - (a.total_sent + a.total_failed));

      setRestaurantStats(stats);
    }

    // Recent messages
    const { data: recent } = await supabase
      .from('whatsapp_messages')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(20);
    setRecentMessages(recent || []);

    setLoading(false);
  }, [restaurants]);

  useEffect(() => {
    loadData();
    checkEvolutionHealth();

    // Realtime for messages
    const channel = supabase
      .channel('admin-whatsapp-monitor')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'whatsapp_messages' }, () => {
        loadData();
      })
      .subscribe();

    // Auto-refresh health every 30s
    const healthInterval = setInterval(checkEvolutionHealth, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(healthInterval);
    };
  }, [loadData, checkEvolutionHealth]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const connectedCount = instances.filter(i => i.status === 'connected').length;
  const totalInstances = instances.length;
  const failRate = todayStats.total > 0 ? ((todayStats.failed / todayStats.total) * 100).toFixed(1) : '0';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-semibold text-lg flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-green-500" />
            Monitoramento WhatsApp
          </h2>
          <p className="text-xs text-muted-foreground">Visão geral dos disparos e conexões em tempo real</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { loadData(); checkEvolutionHealth(); }} className="gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" /> Atualizar
        </Button>
      </div>

      {/* Service Status Banner */}
      <div className={`flex items-center gap-3 p-4 rounded-xl border ${
        evolutionStatus === 'online' 
          ? 'border-green-300/50 bg-green-50 dark:bg-green-950/20 dark:border-green-700/40' 
          : evolutionStatus === 'offline'
          ? 'border-red-300/50 bg-red-50 dark:bg-red-950/20 dark:border-red-700/40'
          : 'border-border bg-muted/50'
      }`}>
        {evolutionStatus === 'checking' && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
        {evolutionStatus === 'online' && <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />}
        {evolutionStatus === 'offline' && <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />}
        <div className="flex-1">
          <p className="text-sm font-medium">
            Evolution API — {evolutionStatus === 'online' ? 'Online' : evolutionStatus === 'offline' ? 'Offline' : 'Verificando...'}
          </p>
          {lastCheck && (
            <p className="text-[10px] text-muted-foreground">
              Última verificação: {format(lastCheck, "HH:mm:ss", { locale: ptBR })}
            </p>
          )}
        </div>
        <Badge variant={evolutionStatus === 'online' ? 'default' : 'destructive'} className={evolutionStatus === 'online' ? 'bg-green-600' : ''}>
          {connectedCount}/{totalInstances} instâncias conectadas
        </Badge>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs text-muted-foreground">Disparos Hoje</p>
              <Send className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold">{todayStats.sent}</p>
            <p className="text-[10px] text-muted-foreground">{weekStats.sent} na semana</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs text-muted-foreground">Falhas Hoje</p>
              <XCircle className="h-4 w-4 text-red-500" />
            </div>
            <p className="text-2xl font-bold">{todayStats.failed}</p>
            <p className="text-[10px] text-muted-foreground">Taxa de falha: {failRate}%</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs text-muted-foreground">Recebidas Hoje</p>
              <ArrowDownRight className="h-4 w-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold">{todayStats.received}</p>
            <p className="text-[10px] text-muted-foreground">{weekStats.received} na semana</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs text-muted-foreground">Instâncias</p>
              <Wifi className="h-4 w-4 text-primary" />
            </div>
            <p className="text-2xl font-bold">{connectedCount}<span className="text-sm text-muted-foreground font-normal">/{totalInstances}</span></p>
            <p className="text-[10px] text-muted-foreground">conectadas</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Top Senders */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="h-4 w-4" /> Ranking de Disparos (7 dias)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {restaurantStats.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Nenhum disparo registrado</p>
            ) : (
              <div className="space-y-2">
                {restaurantStats.slice(0, 10).map((rs, i) => (
                  <div key={rs.restaurant_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50">
                    <span className="text-xs font-bold text-muted-foreground w-5 text-right">{i + 1}.</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{rs.restaurant_name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-green-600 dark:text-green-400 flex items-center gap-0.5">
                          <ArrowUpRight className="h-2.5 w-2.5" /> {rs.total_sent} enviadas
                        </span>
                        {rs.total_failed > 0 && (
                          <span className="text-[10px] text-red-500 flex items-center gap-0.5">
                            <XCircle className="h-2.5 w-2.5" /> {rs.total_failed} falhas
                          </span>
                        )}
                      </div>
                    </div>
                    <Badge variant={rs.connected ? 'default' : 'secondary'} className={`text-[10px] ${rs.connected ? 'bg-green-600' : ''}`}>
                      {rs.connected ? <Wifi className="h-2.5 w-2.5 mr-0.5" /> : <WifiOff className="h-2.5 w-2.5 mr-0.5" />}
                      {rs.connected ? 'Online' : 'Offline'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Instances Status */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Phone className="h-4 w-4" /> Status das Instâncias
            </CardTitle>
          </CardHeader>
          <CardContent>
            {instances.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Nenhuma instância criada</p>
            ) : (
              <div className="space-y-2">
                {instances.map((inst: any) => {
                  const restName = restaurants.find(r => r.id === inst.restaurant_id)?.name || 'Desconhecido';
                  return (
                    <div key={inst.id} className="flex items-center justify-between p-2.5 rounded-lg border border-border">
                      <div>
                        <p className="text-sm font-medium">{restName}</p>
                        <p className="text-[10px] text-muted-foreground font-mono">{inst.instance_name}</p>
                        {inst.phone_number && (
                          <p className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
                            <Phone className="h-2.5 w-2.5" /> +{inst.phone_number}
                          </p>
                        )}
                      </div>
                      <Badge
                        variant={inst.status === 'connected' ? 'default' : inst.status === 'connecting' ? 'secondary' : 'outline'}
                        className={inst.status === 'connected' ? 'bg-green-600' : ''}
                      >
                        {inst.status === 'connected' && <Wifi className="h-3 w-3 mr-1" />}
                        {inst.status === 'connecting' && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                        {inst.status === 'disconnected' && <WifiOff className="h-3 w-3 mr-1" />}
                        {inst.status === 'connected' ? 'Conectado' : inst.status === 'connecting' ? 'Conectando' : 'Desconectado'}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Messages */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Clock className="h-4 w-4" /> Mensagens Recentes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentMessages.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Nenhuma mensagem registrada</p>
          ) : (
            <div className="space-y-1.5 max-h-80 overflow-y-auto">
              {recentMessages.map(msg => {
                const restName = restaurants.find(r => r.id === msg.restaurant_id)?.name || '';
                return (
                  <div key={msg.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/30 text-sm">
                    <div className={`w-2 h-2 rounded-full shrink-0 ${
                      msg.status === 'sent' || msg.status === 'received' ? 'bg-green-500' : 'bg-red-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-xs truncate">{restName}</span>
                        <Badge variant="outline" className="text-[9px] px-1 py-0">
                          {msg.direction === 'outbound' ? '↑ Enviada' : '↓ Recebida'}
                        </Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground truncate">{msg.content}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[10px] text-muted-foreground">+{msg.remote_phone}</p>
                      <p className="text-[9px] text-muted-foreground">
                        {format(new Date(msg.created_at), "HH:mm", { locale: ptBR })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminWhatsAppTab;
